import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { states } from "js/placeholderData/states";
import { isEntityUser } from "js/utilities";
import FormInputError from "components/General/FormInputError";
import MaskedPhoneNumberInput from "components/General/Form/MaskedPhoneNumberInput";
import { useTranslation } from "react-i18next";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";

export default function FACStepTwo({
  formStep,
  setFormStep,
  setFACFormData,
  loggedUser,
}) {
  const uuid = uuidv4();
  const { t } = useTranslation();
  const [selectedState, setSelectedState] = useState({
    value: "all",
  });
  const {
    register: FACStepTwoRegister,
    handleSubmit: FACStepTwoHandleSubmit,
    setValue,
    getValues,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    defaultValues: {
      firstNameClaimer: "",
      lastNameClaimer: "",
      middleNameClaimer: "",
      streetAddressClaimer: "",
      streetAddress2Claimer: "",
      cityClaimer: "",
      stateClaimer: "",
      zipClaimer: "",
      emailClaimer: "",
      relationshipClaimer: "",
      phoneClaimer: "",
    },
  });

  useEffect(() => {
    const defaultValues = {
      firstNameClaimer: loggedUser?.person.firstName,
      lastNameClaimer: loggedUser?.person.lastName,
      middleNameClaimer: loggedUser?.person.middleName,
      streetAddressClaimer: loggedUser?.address.addressLine1,
      streetAddress2Claimer: loggedUser?.address.addressLine2,
      cityClaimer: loggedUser?.address.city,
      stateClaimer: loggedUser?.address.state,
      phoneClaimer: loggedUser?.phone.phoneNumber,
      zipClaimer: loggedUser?.address.postalCode?.substring(0, 5),
      emailClaimer: loggedUser?.email.address,
      relationshipClaimer: "Contract Owner",
    };
    reset(defaultValues);
    const formState = getValues("stateClaimer");
    const matchedState = states.find(
      (state) => state.abbreviation === formState
    );
    setSelectedState(
      matchedState
        ? {
            value: matchedState.abbreviation,
            displayAs: matchedState.name,
            selectedPolicyDisplayAs: matchedState.name,
            isDisplayOnly: true,
          }
        : { value: "all" }
    );
  }, [loggedUser, reset]);

  const onSubmit = (data) => {
    // screenScrollTo();
    setFormStep(3);
    setFACFormData((currentFormData) => {
      return {
        ...currentFormData,
        ...data,
      };
    });
  };

  return (
    <>
      {formStep === 2 && (
        <>
          <p className="text-xs">{t("FACStep-two-title")} </p>
          <form onSubmit={FACStepTwoHandleSubmit(onSubmit)}>
            <div>
              <hr />
              <div className="grid gap-4 grid-cols-1 lg:gap-6">
                <div
                  className={`grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-${
                    !isEntityUser(loggedUser?.person?.taxType)
                      ? "[2fr,2fr,1fr]"
                      : "[1.5fr,1fr,1fr]"
                  }`}
                >
                  {!isEntityUser(loggedUser?.person?.taxType) && (
                    <div>
                      <label
                        htmlFor={`${uuid}-fn-claimer`}
                        className="block mb-1"
                      >
                        {t("FACStep-two-fn")}
                      </label>
                      <input
                        id={`${uuid}-fn-claimer`}
                        disabled
                        type="text"
                        {...FACStepTwoRegister("firstNameClaimer", {
                          required: "Please enter a first name",
                        })}
                      />
                      <FormInputError
                        id={`${uuid}-fn-claimer-error`}
                        errorMessage={errors.firstNameClaimer?.message}
                      />
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor={`${uuid}-ln-claimer`}
                      className="block mb-1"
                    >
                      {t("FACStep-two-ln")}
                    </label>
                    <input
                      id={`${uuid}-ln-claimer`}
                      disabled
                      type="text"
                      {...FACStepTwoRegister("lastNameClaimer", {
                        required: "Please enter a last name",
                      })}
                    />
                    <FormInputError
                      id={`${uuid}-ln-claimer-error`}
                      errorMessage={errors.lastNameClaimer?.message}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`${uuid}-mn-claimer`}
                      className="block mb-1"
                    >
                      {t("FACStep-two-midddle")}
                    </label>
                    <input
                      id={`${uuid}-mn-claimer`}
                      disabled
                      type="text"
                      {...FACStepTwoRegister("middleNameClaimer")}
                    />
                    <FormInputError
                      id={`${uuid}-mn-claimer-error`}
                      errorMessage={errors.middleNameClaimer?.message}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor={`${uuid}-sa-claimer`} className="block mb-1">
                    {t("FACStep-two-mailling-ads-1")}
                  </label>
                  <input
                    id={`${uuid}-sa-claimer`}
                    disabled
                    type="text"
                    {...FACStepTwoRegister("streetAddressClaimer")}
                  />
                  <FormInputError
                    id={`${uuid}-sa-claimer-error`}
                    errorMessage={errors.streetAddressClaimer?.message}
                  />
                </div>

                <div>
                  <label htmlFor={`${uuid}-sa2-claimer`} className="block mb-1">
                    {t("FACStep-two-mailling-ads-2")}
                  </label>
                  <input
                    id={`${uuid}-sa2-claimer`}
                    disabled
                    type="text"
                    {...FACStepTwoRegister("streetAddress2Claimer")}
                  />
                  <FormInputError
                    id={`${uuid}-sa2-claimer-error`}
                    errorMessage={errors.streetAddress2Claimer?.message}
                  />
                </div>
                <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1.5fr,1fr,1fr]">
                  <div>
                    <label
                      htmlFor={`${uuid}-city-claimer`}
                      className="block mb-1"
                    >
                      {t("FACStep-two-city")}
                    </label>
                    <input
                      id={`${uuid}-city-claimer`}
                      type="text"
                      disabled
                      {...FACStepTwoRegister("cityClaimer")}
                    />
                    <FormInputError
                      id={`${uuid}-city-claimer-error`}
                      errorMessage={errors.cityClaimer?.message}
                    />
                  </div>

                  <div>
                    <Controller
                      name="stateClaimer"
                      control={control}
                      render={() => (
                        <RELEASING_AutoCompleteComboBox
                          labelName={t("FACStep-two-state")}
                          inputPlaceholderText={t("FACStep-two-state")}
                          options={[
                            {
                              value: "all",
                              displayAs: t("FACStep-two-state"),
                              selectedPolicyDisplayAs: t("FACStep-two-state"),
                              isDisplayOnly: true,
                            },
                            ...states.map((state) => ({
                              value: state.abbreviation,
                              displayAs: state.name,
                              selectedPolicyDisplayAs: state.name,
                              isDisplayOnly: true,
                            })),
                          ]}
                          selectedOption={selectedState}
                          setSelectedOption={(option) => {
                            setSelectedState(option);
                            if (option?.value !== "all") {
                              setValue("stateClaimer", option.value);
                            } else {
                              setValue("stateClaimer", "");
                            }
                          }}
                          readOnly={true}
                        />
                      )}
                    />

                    <FormInputError
                      id={`${uuid}-state-claimer-error`}
                      errorMessage={errors.stateClaimer?.message}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`${uuid}-zip-claimer`}
                      className="block mb-1"
                    >
                      {t("FACStep-two-zipcode")}
                    </label>
                    <input
                      id={`${uuid}-zip-claimer`}
                      type="text"
                      disabled
                      {...FACStepTwoRegister("zipClaimer")}
                    />
                    <FormInputError
                      id={`${uuid}-zip-claimer-error`}
                      errorMessage={errors.zipClaimer?.message}
                    />
                  </div>
                </div>
                <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1.5fr,1fr,1fr]">
                  <div>
                    <label id={`${uuid}-phone-claimer`} className="block mb-1">
                      {t("FACStep-two-pn")}
                    </label>
                    <MaskedPhoneNumberInput
                      name="phoneClaimer"
                      describedby={`${uuid}-phone-claimer`}
                      register={FACStepTwoRegister}
                      setValue={setValue}
                      defaultValue={getValues("phoneClaimer")}
                      required={false}
                      disabled
                    />
                    <FormInputError
                      id={`${uuid}-phone-claimer-error`}
                      errorMessage={errors.phoneClaimer?.message}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`${uuid}-email-claimer`}
                      className="block mb-1"
                    >
                      {t("FACStep-two-email")}
                    </label>
                    <input
                      id={`${uuid}-email-claimer`}
                      type="text"
                      disabled
                      {...FACStepTwoRegister("emailClaimer")}
                    />

                    <FormInputError
                      id={`${uuid}-email-claimer-error`}
                      errorMessage={errors.emailClaimer?.message}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor={`${uuid}-relationship-claimer`}
                    className="block mb-1"
                  >
                    {t("FACStep-two-relationship")}
                  </label>
                  <input
                    id={`${uuid}-relationship-claimer`}
                    type="text"
                    disabled
                    {...FACStepTwoRegister("relationshipClaimer", {
                      required:
                        "Please select your relationship with the deceased",
                    })}
                  />
                  <FormInputError
                    id={`${uuid}-relationship-claimer`}
                    errorMessage={errors.relationshipClaimer?.message}
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    type="button"
                    onClick={() => setFormStep(1)}
                  >
                    {t("FACStep-two-btn-1")}
                  </button>

                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    {t("FACStep-two-btn-2")}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </>
      )}
    </>
  );
}
